import { AppId } from '@wix/cashier-common/dist/src/enums/AppId';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { ServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionStatus';
import { ServerTransactionType } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionType';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import { BuyerService } from '@wix/cashier-core/dist/src/services/Buyer';
import { IPublicCreditCard } from '@wix/cashier-core/dist/src/services/CreditCard/types';
import {
  IMandatoryField,
  MandatoryFieldDomain,
} from '@wix/cashier-core/dist/src/services/MandatoryFields/types';
import { PaymentService } from '@wix/cashier-core/dist/src/services/Payment';
import {
  IRequestPaymentDetailsResponse,
  RedirectTarget,
} from '@wix/cashier-core/dist/src/services/Payment/types';
import { PaymentMethodService } from '@wix/cashier-core/dist/src/services/PaymentMethod';
import {
  IPaymentMethod,
  IPaymentMethodRequestParams,
  IPaymentMethodsResponse,
} from '@wix/cashier-core/dist/src/services/PaymentMethod/types';
import { CreditCardFormAPI } from '@wix/payments-cc-form/dist/lib/CreditCardFormAPI';
import { CardPublicData } from '@wix/payments-cc-form/dist/src/types/CardPublicData';
import { TokenizeResponse } from '@wix/payments-cc-form/dist/src/types/TokenizeResponse';
import { EventEmitter } from 'events';
import { Experiment } from '../types/Experiment';
import { getBiLogger, updateBiDefaults } from '../utils/biLogger';
import { t } from '../utils/i18n';
import {
  ICashierSDKConfiguration,
  ICashierSDKPublicApi,
  IContinuePaymentParams,
  IFetchSettingsResponse,
  ValidateResult,
} from './cashier-sdk';
import MethodFactory from './MethodFactory';
import CreditCard from './modules/web/CreditCard';
import PaymentModule, {
  events as PaymentModuleEvents,
} from './modules/web/PaymentModule';
import { MFieldsFlat } from './types/MField';
import {
  PaymentCompleteResult,
  PaymentFailResult,
} from './types/PaymentCompleteResult';
import { buildPaymentDetails } from './utils/buildPaymentDetails';
import {
  createExpectedError,
  CreateExpectedErrorParams,
  ExpectedError,
  isExpectedError,
} from './utils/expectedError';
import { ignoreCallbackFailures } from './utils/ignoreCallbackFailures';
import { isAPM } from './utils/isAPM';
import { isPayModalEnabled } from './utils/isPayModalEnabled';
import { buildLinguistHeader, LinguistHeaderKey } from './utils/linguistUtils';
import { nestMFields } from './utils/nestMFields';
import {
  createPaymentCompleteResultBuilder,
  isPaymentCompleteResult,
  PaymentCompleteResultBuilder,
} from './utils/paymentCompleteResult';
import {
  mapErrorCodeToMessage,
  ServerErrorCode,
} from './utils/serverErrorMapping';
import { filterDefined } from '../utils/filterDefined';

export const defaultConfig: ICashierSDKConfiguration = {
  query: {},
  preferNewTabOverRedirect: false,
  // default url is necessary when SDK is used directly (not via Widget), which is the case for Stores Cart page
  // default url redirects to same page that Widget pass in "processingPageUrl"
  // the reason why we accept the url from the Widget and not always using the default one is to avoid
  // extra redirect (settings-server -> resolve Widget version -> redirect to the page in Widget's CDN)
  processingPageUrl: `https://cashier.wixapps.net/cashier-settings-server/payment-processing`,

  allowInPersonPayment: false,
  allowManualPayment: false,
  allowRecurringPaymentOnly: false,
};

export interface SetActiveCreditCardProps
  extends Partial<
    Pick<
      IPublicCreditCard,
      | 'network'
      | 'billingAddress'
      | 'expired'
      | 'expiration'
      | 'additionalInformation'
    >
  > {}

export const events = { ...PaymentModuleEvents };

export interface IPayParams {
  instance: string;
  saveCreditCard?: boolean;
  creditCardId?: string;
  methodTitle?: string;
  frameable: boolean;
  responsive: boolean;
  redirectable?: boolean;
  isMerchantContext?: boolean;
  mFieldsFlat: MFieldsFlat;
  installments?: number;
  providerId: PaymentProvider;
  orderId?: string;
}

export interface IFetchPaymentMethodsConfiguration {
  appId: string;
  appInstanceId: string;
  cashierAppInstanceId?: string;
  snapshotId?: string;
  country?: string;
  locale?: string;
  lang?: string;
  isPrimaryLanguage?: boolean;
  singlePM?: PaymentMethod;
  appInstance?: string;
}

interface IFetchPaymentMethodsInternal {
  host: string;
  configuration: IFetchPaymentMethodsConfiguration;
  countryCode3?: string;
  noPopulate?: boolean;
  appInstance?: string;
}

export class PaymentsSDK implements ICashierSDKPublicApi {
  private eventEmitter: EventEmitter;
  private paymentModule: PaymentModule;
  private props: ICashierSDKConfiguration = {};
  private activePaymentMethod: PaymentMethod;
  private activeSavedCreditCard: string;
  private methodFactory: MethodFactory;
  private initializePromise: Promise<any>;
  private cardFormApi: CreditCardFormAPI;

  public addMethod = (methodId: string, props?: any) => {
    const _props = props || {};
    return this.methodFactory.addMethod(methodId, {
      localizeError: this.localizeError,
      ...this.props,
      ..._props,
      paymentModule: this.paymentModule,
      getCardFormApi: () => this.cardFormApi,
      sdkInitConfig: this.props,
    });
  };

  private getMethodIconFallback = (methodName: PaymentMethod): string => {
    if (methodName === PaymentMethod.Sofort) {
      return t('icons.sofortKlarna');
    }

    const fallbackIconKey = `icons.${methodName}`;

    if (t(fallbackIconKey) !== fallbackIconKey) {
      return t(fallbackIconKey);
    }
  };

  private fixupPMethods = (pMethods: IPaymentMethod[]): IPaymentMethod[] =>
    pMethods.map((pMethod) => {
      const methodIcon =
        pMethod.methodIcon ||
        this.getMethodIconFallback(pMethod.paymentMethod as PaymentMethod);
      let frameable = pMethod.frameable;
      // We need to force enable frameable to `true` so we can test it even if BE return `false`
      if (
        isAPM(pMethod.paymentMethod as PaymentMethod) &&
        experimentsService.isExperimentEnabled(Experiment.APMsInModal)
      ) {
        frameable = true;
      }

      let mandatoryFields: IMandatoryField[] = pMethod.mandatoryFields;

      if (
        pMethod.paymentMethod === PaymentMethod.CreditCard &&
        pMethod.connectedProviderId === PaymentProvider.Square &&
        experimentsService.isExperimentEnabled(
          Experiment.EnableSquareVerifyBuyer
        )
      ) {
        mandatoryFields = mandatoryFields || [];
        // Server returns only the fields that are required by the PSP and (!) are missing in the Order.
        // This means that some fields won't be known (by the client) as "mandatory" if they are already in the Order.
        // In case of Square, to verify buyer, we need to know certain fields, thus we want to ensure that they are required
        // so that User will enter them (or we'll get them from TPA) and we can pass their values to Square during the "verifyBuyer" call.
        const requiredSquareMFieldsToVerifyBuyer = ['firstName', 'lastName'];

        requiredSquareMFieldsToVerifyBuyer.forEach((clientFieldName) => {
          if (
            !mandatoryFields.some((mField) =>
              mField.fieldName.endsWith(`.${clientFieldName}`)
            )
          ) {
            mandatoryFields.push({
              fieldName: `description.billingAddress.${clientFieldName}`,
              domain: MandatoryFieldDomain.Order,
              values: [],
            });
          }
        });
      }

      return {
        ...pMethod,
        ...filterDefined({
          frameable,
          methodIcon,
          mandatoryFields,
        }),
      };
    });

  private populateMethodFactory = (pMethods: IPaymentMethod[]) => {
    pMethods?.forEach((paymentMethod) => {
      let squareAppId = '';
      if (
        paymentMethod?.paymentMethod === PaymentMethod.CreditCard &&
        paymentMethod.additionalInfo
      ) {
        squareAppId = paymentMethod.additionalInfo.squareAppId;
      }
      const query = {
        ...this.props.query,
        providerId: paymentMethod.connectedProviderId,
        squareAppId: squareAppId || '',
        ...(paymentMethod.additionalInfo || {}),
      };
      this.addMethod(paymentMethod.paymentMethod, {
        ...this.props,
        query,
        paymentModule: this.paymentModule,
      });
    });
  };

  private filterPMethods = (
    unfilteredPMethods: IPaymentMethod[]
  ): IPaymentMethod[] => {
    if (!unfilteredPMethods) {
      return [];
    }

    const excludedPaymentMethods: PaymentMethod[] = [
      PaymentMethod.Cash,
      PaymentMethod.POS,
      PaymentMethod.SquarePOS,
    ];

    if (!this.props.allowInPersonPayment) {
      excludedPaymentMethods.push(PaymentMethod.InPerson);
    }

    if (!this.props.allowManualPayment) {
      excludedPaymentMethods.push(PaymentMethod.Offline);
    }

    let pMethods = unfilteredPMethods.filter(
      ({ paymentMethod }) =>
        !excludedPaymentMethods.includes(paymentMethod as PaymentMethod)
    );

    // TODO: can be removed once server will filter PMs using passed "chargeIntent" query param (https://jira.wixpress.com/browse/PAY-7515)
    if (this.props.allowRecurringPaymentOnly) {
      pMethods = pMethods.filter((pm) =>
        pm.supportedChargeIntents?.includes(ServerTransactionType.Recurring)
      );
    }

    if (
      this.props.appId === AppId.ArtStore &&
      !experimentsService.isExperimentEnabled(
        Experiment.EnablePixBitForArtStores
      )
    ) {
      const artStoreExcludePaymentMethods: PaymentMethod[] = [
        PaymentMethod.Pix,
        PaymentMethod.Bit,
      ];
      pMethods = pMethods.filter(
        ({ paymentMethod }) =>
          !artStoreExcludePaymentMethods.includes(
            paymentMethod as PaymentMethod
          )
      );
    }

    return pMethods;
  };

  private fetchPaymentMethods = async (
    args: IFetchPaymentMethodsInternal
  ): Promise<IPaymentMethod[]> => {
    const host = args.host || this.props.host;
    const paymentMethodService = new PaymentMethodService({
      baseURL: host,
      appInstance: args.appInstance,
      revalidateAppInstance: this.props.revalidateAppInstance,
    });
    const { configuration } = args;
    const { query } = this.props;

    const params: IPaymentMethodRequestParams = {};

    if (this.props.allowRecurringPaymentOnly) {
      params.chargeIntent = ServerTransactionType.Recurring;
    }

    if (!configuration.singlePM) {
      params.allowManualPayment = this.props.allowManualPayment;
      params.allowInPersonPayment = this.props.allowInPersonPayment;
    }

    if (args.countryCode3) {
      params.country = args.countryCode3;
    }
    if (query) {
      if (query.amount) {
        params.amount = query.amount;
      }

      if (query.locale) {
        params.locale = query.locale;
      }
    }

    if (
      configuration.cashierAppInstanceId &&
      configuration.locale &&
      configuration.lang &&
      configuration.isPrimaryLanguage !== undefined
    ) {
      paymentMethodService.setHeaders({
        [LinguistHeaderKey]: buildLinguistHeader({
          locale: configuration.locale,
          lang: configuration.lang,
          isPrimaryLanguage: configuration.isPrimaryLanguage,
          appInstanceId: configuration.cashierAppInstanceId,
        }),
      });
    }

    const orderId = configuration.snapshotId;
    const accountId = `${configuration.appId}:${configuration.appInstanceId}`;

    let pMethods: IPaymentMethod[];

    if (configuration.singlePM) {
      const paymentMethod = configuration.singlePM;
      if (orderId) {
        pMethods = [
          await paymentMethodService.getPaymentMethodOrder(
            orderId,
            paymentMethod,
            params
          ),
        ];
      } else {
        pMethods = [
          await paymentMethodService.getPaymentMethodAccount(
            accountId,
            paymentMethod,
            params
          ),
        ];
      }
    } else if (orderId) {
      pMethods = await paymentMethodService.fetchPaymentMethodsOrder(
        orderId,
        params
      );
    } else {
      pMethods = await paymentMethodService.fetchPaymentMethodsAccount(
        accountId,
        params
      );
    }

    await experimentsService.ready();
    pMethods = this.filterPMethods(pMethods);
    await this.props.i18nPromise;
    pMethods = this.fixupPMethods(pMethods);

    if (!args.noPopulate) {
      this.populateMethodFactory(pMethods);
    }

    return pMethods;
  };

  public fetchPaymentMethodsByConfiguration = async (
    configuration: IFetchPaymentMethodsConfiguration,
    country = '',
    host = this.props.host
  ): Promise<IPaymentMethodsResponse> => {
    return {
      paymentMethods: await this.fetchPaymentMethods({
        host,
        noPopulate: true,
        configuration,
        countryCode3: country,
      }),
    };
  };

  public fetchSettings = async (
    configuration: IFetchPaymentMethodsConfiguration,
    host = this.props.host
  ): Promise<IFetchSettingsResponse> => {
    if (
      !(configuration.appInstanceId && configuration.appId) &&
      !configuration.snapshotId
    ) {
      return Promise.reject({
        status: 'FAILURE',
        translatedError:
          'Need to pass configuration.appInstanceId and configuration.appId or configuration.snapshotId',
      });
    }

    let countryCode = configuration.country;
    if (!countryCode) {
      try {
        const countryResponse = await this.resolveCountry(
          host,
          configuration.appInstance
        );
        countryCode = countryResponse.country;
        this.setCountry(countryCode);
      } catch (e) {
        console.warn('Country was not resolved.');
      }
    }

    try {
      const paymentMethods = await this.fetchPaymentMethods({
        host,
        configuration,
        countryCode3: countryCode,
        appInstance: configuration.appInstance,
      });
      return {
        countryCode,
        paymentMethods,
      };
    } catch (e) {
      return Promise.reject({
        status: 'FAILURE',
        errorCode: '500',
        translatedError: '',
      });
    }
  };

  public setActivePaymentMethod = (pmId, eventType?: string) => {
    this.activePaymentMethod = pmId;
    this.activeSavedCreditCard = undefined;
    this.props.onPaymentMethodChanged?.(pmId, {
      type: 'PAYMENT_METHOD_CHANGED',
      pmId,
      eventType,
    });
  };

  public setActiveCreditCard = (
    creditCardId: string,
    creditCardDetails: SetActiveCreditCardProps = {},
    eventType?: string
  ) => {
    this.activeSavedCreditCard = creditCardId;
    this.activePaymentMethod = PaymentMethod.CreditCard;

    this.props.onCreditCardChanged?.(
      creditCardId,
      creditCardDetails,
      { type: 'SAVED_CREDIT_CARD_CHANGED', creditCardId },
      eventType
    );
  };

  public init = async (configuration: ICashierSDKConfiguration = {}) => {
    this.props = { ...defaultConfig, ...configuration };
    ignoreCallbackFailures(this.props);

    // in case if some legacy integration (Stores Cart page for PayPal?) still uses SDK directly, and passes "true" to one of these flags
    if (
      // @ts-ignore
      configuration.walletPaymentInNewTab ||
      // @ts-ignore
      configuration.creditCardPaymentInNewTab
    ) {
      this.props.preferNewTabOverRedirect = true;
    }

    const { appId, appInstanceId, snapshotId, msid, visitorId } = this.props;

    this.eventEmitter = configuration.eventEmitter || new EventEmitter();
    this.methodFactory = new MethodFactory();
    this.paymentModule = new PaymentModule(this.props, this.eventEmitter);
    const biDefaults = {
      appId,
      appInstanceId,
      visitorId,
      msid,
      orderId: snapshotId,
    };

    updateBiDefaults(biDefaults);
    this.initializePromise = Promise.all<any>([
      this.props.i18nPromise,
      experimentsService.ready(),
    ]);
    await this.initializePromise;

    if (
      isPayModalEnabled(configuration.appId as AppId) &&
      !configuration.openPayModal
    ) {
      console.warn(`SDK.init :: openPayModal wasn't passed to SDK.init`);
    }
  };

  public on = (event: string, handler: (...args: any[]) => any) => {
    this.eventEmitter.on(event, handler);
  };

  public off = (event: string, handler: (...args: any[]) => any) => {
    this.eventEmitter.removeListener(event, handler);
  };

  public submit = (
    method: PaymentMethod,
    params: IPayParams
  ): Promise<PaymentCompleteResult> => this.pay(method, params);

  public expand = (method?: PaymentMethod) => {
    const foundMethod = this.methodFactory.getMethod(
      method || this.activePaymentMethod
    );
    if (foundMethod?.expand) {
      return foundMethod.expand(method || this.activePaymentMethod);
    }
    return Promise.reject({ code: 404 });
  };

  public collapse = (method?: PaymentMethod) => {
    const foundMethod = this.methodFactory.getMethod(
      method || this.activePaymentMethod
    );
    if (foundMethod?.collapse) {
      return foundMethod.collapse(method || this.activePaymentMethod);
    }
    return Promise.reject({ code: 404 });
  };

  public validate = (method?: PaymentMethod): Promise<ValidateResult> => {
    const foundMethod = this.methodFactory.getMethod(
      method || this.activePaymentMethod
    );
    if (foundMethod) {
      return foundMethod.validate(method || this.activePaymentMethod);
    }

    return Promise.reject({ code: 404 });
  };

  public continuePayment = async (
    paymentMethod: PaymentMethod,
    params: IContinuePaymentParams
  ): Promise<PaymentCompleteResult> => {
    const {
      creditCardId = this.activeSavedCreditCard,
      mFieldsFlat = {},
      isMerchantContext,
      methodTitle,
    } = params;
    try {
      if (!this.methodFactory.hasMethod(paymentMethod)) {
        throw createExpectedError({
          errorMessage: `Failed to retrieve a payment module for "${paymentMethod}" payment method`,
        });
      }

      const result = await this.paymentModule.continuePayment(paymentMethod, {
        chargeResponse: PaymentService.parseResponseToken(params.responseToken),
        inNewTab: params.inNewTab,
        saveCreditCard: params.saveCreditCard,
        instance: params.instance,
        creditCardId,
        frameable: params.frameable,
        responsive: params.responsive,
        mFieldsNested: params.mFieldsNested || nestMFields(mFieldsFlat),
      });

      this.props.onPaymentComplete?.(paymentMethod, result);

      return result;
    } catch (error) {
      const paymentResultBuilder = createPaymentCompleteResultBuilder()
        .withPaymentMethod(paymentMethod)
        .withChargeInitiated();

      await this.enhancePaymentCompleteResultWithError({
        paymentResultBuilder,
        error,
        isMerchantContext,
        localizedErrorParams: {
          paymentMethod: methodTitle,
        },
      });

      const result = paymentResultBuilder.build() as PaymentFailResult;

      this.props.onError?.(paymentMethod, {
        type: 'PAYMENT_ERROR',
        error: result,
      });

      this.props.onPaymentComplete?.(paymentMethod, result);

      throw result;
    }
  };

  public getCreditCardPublicData = (): Promise<CardPublicData> => {
    const creditCardModule = this.methodFactory.getMethod(
      PaymentMethod.CreditCard
    ) as CreditCard;

    if (!creditCardModule) {
      throw new Error(
        `sdk.getCreditCardPublicData should be called only after CC module was added (PMs were fetched)`
      );
    }

    return creditCardModule.getCreditCardPublicData();
  };

  public tokenize = (params: {
    instance: string;
    providerId: PaymentProvider;
    mFieldsFlat?: MFieldsFlat;
  }): Promise<TokenizeResponse> => {
    const creditCardModule = this.methodFactory.getMethod(
      PaymentMethod.CreditCard
    );

    if (!creditCardModule) {
      return Promise.reject(
        createExpectedError({
          errorMessage: `Failed to retrieve a payment module for "${PaymentMethod.CreditCard}" payment method`,
        })
      );
    }

    const { instance, mFieldsFlat = {}, providerId } = params;

    return creditCardModule.tokenize({
      instance,
      providerId,
      ...(mFieldsFlat && { mFieldsNested: nestMFields(mFieldsFlat) }),
    });
  };

  public localizeError = async (
    errorCode: string,
    isMerchantContext?: boolean,
    messageParams?: {
      [param: string]: string;
    }
  ): Promise<string> => {
    try {
      return mapErrorCodeToMessage(errorCode, {
        isMerchantContext,
        messageParams,
      });
    } catch (error) {
      return '';
    }
  };

  private enhancePaymentCompleteResultWithError = async (params: {
    paymentResultBuilder: PaymentCompleteResultBuilder;
    error: any;
    isMerchantContext: boolean;
    localizedErrorParams?: {
      [param: string]: string;
    };
  }): Promise<void> => {
    const {
      paymentResultBuilder,
      error,
      isMerchantContext,
      localizedErrorParams,
    } = params;

    if (isPaymentCompleteResult(error)) {
      paymentResultBuilder.mergeResult(error);
    } else if (isExpectedError(error)) {
      const expectedError: ExpectedError = error;

      if (expectedError.serverErrorCode) {
        paymentResultBuilder.withErrorCode(expectedError.serverErrorCode);
      }

      paymentResultBuilder.withInternalError(
        filterDefined({
          errorMessage: expectedError.errorMessage,
          errorData: expectedError.errorData,
        })
      );
    } else {
      paymentResultBuilder.withUnexpectedInternalError(error);
    }

    if (!paymentResultBuilder.build().serverStatus) {
      paymentResultBuilder.withServerStatus(ServerTransactionStatus.Declined); // should be "Failed" by default?
    }

    if (!(paymentResultBuilder.build() as PaymentFailResult).errorCode) {
      paymentResultBuilder.withErrorCode(ServerErrorCode.Default);
    }

    paymentResultBuilder.withErrorMessage(
      await this.localizeError(
        (paymentResultBuilder.build() as PaymentFailResult).errorCode,
        isMerchantContext,
        localizedErrorParams
      )
    );
  };

  public createPaymentFailResult = async (params: {
    paymentMethod: PaymentMethod;
    isMerchantContext: boolean;
    error: CreateExpectedErrorParams;
  }): Promise<PaymentFailResult> => {
    const paymentResultBuilder =
      createPaymentCompleteResultBuilder().withPaymentMethod(
        params.paymentMethod
      );

    await this.enhancePaymentCompleteResultWithError({
      paymentResultBuilder,
      isMerchantContext: params.isMerchantContext,
      error: createExpectedError(params.error),
    });

    return paymentResultBuilder.build() as PaymentFailResult;
  };

  public prepareToPay = async (params: {
    pMethodData: IPaymentMethod;
  }): Promise<void> => {
    const { pMethodData } = params;

    if (pMethodData.redirectable) {
      const paymentMethod = pMethodData.paymentMethod as PaymentMethod;
      const redirectTarget = this.paymentModule.getRedirectTarget({
        paymentMethod,
        frameable: pMethodData.frameable,
      });

      if (redirectTarget === RedirectTarget.NewWindow) {
        this.paymentModule.preOpenPaymentPopup(paymentMethod);
      }
    }
  };

  public cancelPayment = async (): Promise<void> => {
    this.paymentModule.closePaymentPopup();
  };

  public pay = async (
    method: PaymentMethod,
    params: IPayParams
  ): Promise<PaymentCompleteResult> => {
    const {
      instance,
      creditCardId = this.activeSavedCreditCard,
      saveCreditCard,
      methodTitle,
      frameable,
      responsive,
      redirectable,
      isMerchantContext,
      mFieldsFlat,
      installments,
      providerId,
      orderId,
    } = params;
    const paymentMethod = method || this.activePaymentMethod;
    const paymentResultBuilder =
      createPaymentCompleteResultBuilder().withPaymentMethod(paymentMethod);

    try {
      const paymentMethodModule = this.methodFactory.getMethod(paymentMethod);

      if (!paymentMethodModule) {
        throw createExpectedError({
          errorMessage: `Failed to retrieve a payment module for "${paymentMethod}" payment method`,
        });
      }

      const result = await paymentMethodModule.pay(nestMFields(mFieldsFlat), {
        paymentMethod,
        instance,
        revalidateAppInstance: this.props.revalidateAppInstance,
        saveCreditCard,
        creditCardId,
        frameable,
        responsive,
        redirectable,
        installments,
        providerId,
        orderId,
        onBeforeCharge: () => {
          paymentResultBuilder.withChargeInitiated();
        },
      });

      paymentResultBuilder.mergeResult(result);
    } catch (error) {
      await this.enhancePaymentCompleteResultWithError({
        paymentResultBuilder,
        error,
        isMerchantContext,
        localizedErrorParams: {
          paymentMethod: methodTitle,
        },
      });

      this.props.onError?.(method, {
        type: 'PAYMENT_ERROR',
        error: paymentResultBuilder.build() as PaymentFailResult,
      });
    }

    this.props.onPaymentComplete?.(paymentMethod, paymentResultBuilder.build());

    return paymentResultBuilder.build();
  };

  public initializePayment = async (
    method?: PaymentMethod,
    host: string = this.props.host,
    additionalPaymentDetails: {
      creditCardId?: string;
      billingAddress?: any;
      additionalInformation?: any;
      providerId?: PaymentProvider;
    } = {
      creditCardId: null,
      billingAddress: null,
      additionalInformation: null,
    },
    params?: {
      frameable?: boolean;
      redirectable?: boolean;
      instance?: string;
      isMerchantContext?: boolean;
      mFieldsFlat: MFieldsFlat;
      installments?: number;
      saveCreditCard?: boolean;
    }
  ): Promise<IRequestPaymentDetailsResponse> => {
    const {
      instance,
      frameable = false,
      mFieldsFlat = {},
      installments,
      redirectable = true,
      isMerchantContext,
      saveCreditCard,
    } = params || {};
    const paymentMethod = method || this.activePaymentMethod;
    const paymentResultBuilder =
      createPaymentCompleteResultBuilder().withPaymentMethod(paymentMethod);

    try {
      const paymentMethodModule = this.methodFactory.getMethod(paymentMethod);

      if (!paymentMethodModule) {
        throw createExpectedError({
          errorMessage: `Failed to retrieve a payment module for "${paymentMethod}" payment method`,
        });
      }

      const buildPaymentDetailsParams: Parameters<
        typeof buildPaymentDetails
      >[0] = {
        paymentMethod,
        mFieldsNested: nestMFields(mFieldsFlat),
        visitorId: this.props.visitorId,
        locale: this.props.query.locale,
      };

      if (redirectable) {
        buildPaymentDetailsParams.redirectTarget =
          this.paymentModule.getRedirectTarget({
            paymentMethod,
            frameable,
          });
      }

      if (paymentMethod === PaymentMethod.CreditCard) {
        buildPaymentDetailsParams.installments = installments;

        buildPaymentDetailsParams.tokenizeResponse = await this.tokenize({
          instance,
          mFieldsFlat,
          providerId: additionalPaymentDetails?.providerId,
        });

        if (saveCreditCard) {
          buildPaymentDetailsParams.saveCreditCard = true;
        }
      }

      const details = await buildPaymentDetails(buildPaymentDetailsParams);

      return await new PaymentService({
        baseURL: host,
        appInstance: instance,
        revalidateAppInstance: this.props.revalidateAppInstance,
      }).createPayment({ details });
    } catch (error) {
      await this.enhancePaymentCompleteResultWithError({
        paymentResultBuilder,
        error,
        isMerchantContext,
      });

      const result = paymentResultBuilder.build() as PaymentFailResult;

      this.props.onError?.(paymentMethod, {
        type: 'PAYMENT_ERROR',
        error: result,
      });

      throw result;
    }
  };

  public resolveCountry = async (
    host: string = this.props.host,
    appInstance?: string
  ) => {
    if (this.props?.query?.country) {
      return {
        country: this.props.query.country,
        status: 'OK',
        errorCode: '',
        translatedError: '',
      };
    }

    try {
      const country = await new BuyerService({
        baseURL: host,
        appInstance,
        revalidateAppInstance: this.props.revalidateAppInstance,
      }).resolveCountry();
      return {
        country,
        status: 'OK',
        errorCode: '',
        translatedError: '',
      };
    } catch (e) {
      return Promise.reject({
        country: '',
        status: 'FAILURE',
        errorCode: '500',
        translatedError: '',
      });
    }
  };

  public setCountry = (country: string) => {
    if (this.props?.query) {
      this.props.query.country = country;
    } else if (this.props && !this.props.query) {
      this.props.query = { country };
    }
  };

  public logMandatoryFieldsValidationStatus = (data) => {
    getBiLogger().checkOutPaymentMethodValidationCompleted({
      ...data,
      orderSnapshotId: this.props.snapshotId,
    });
  };

  public getProps = (): ICashierSDKConfiguration => this.props;

  public setCardFormApi = (api: CreditCardFormAPI): void => {
    this.cardFormApi = api;
  };
}

export const paymentsSDK = new PaymentsSDK();
